/**
 * One assessed control (ADR-0008/0018): big Yes / No / N/A buttons, the
 * comment that becomes mandatory per the control's rules, evidence photos,
 * help text, analyst flags (returned surveys) and carried-forward findings.
 *
 * The selected answer is coloured by compliance, not by Yes/No: for an
 * inverted control ("Is the barrier easily climbed?") a Yes turns red.
 */
import { useCallback, useState } from 'react';
import {
  isNonCompliant,
  localize,
  polishAndTranslateObservation,
  type ControlAnswer,
  type ControlItem,
  type ControlValue,
} from '@sss/form-engine';
import { Badge, Banner, Button, Card, Field, Icon, Segmented, SEVERITY_TONE, Textarea } from '../../components';
import { useServices } from '../../app/services';
import { useToast } from '../../components/Toast';
import { cx } from '../../lib/cx';
import { useI18n } from '../../lib/i18n';
import type { CarriedFinding } from '../../lib/api/types';
import { HelpButton } from './FieldRenderer';
import { FloorplanPinModal } from './FloorplanPinModal';
import { PhotoInput } from './PhotoInput';
import { TagScanner } from './TagScanner';
import type { CaptureActions } from './types';
import { VoiceNoteButton } from './VoiceNoteButton';

export interface ControlRequirements {
  nonCompliant: boolean;
  commentRequired: boolean;
  photoRequired: boolean;
  /** Why the comment is required, for the inline message. */
  commentReason: 'NON_COMPLIANT' | 'NA' | null;
}

export function controlRequirements(control: Pick<ControlItem, 'compliantAnswer' | 'requireCommentOn' | 'requirePhotoOn'>, value: ControlValue | null): ControlRequirements {
  const nonCompliant = isNonCompliant(control, value);
  const naComment = value === 'NA' && control.requireCommentOn.includes('NA');
  const ncComment = nonCompliant && control.requireCommentOn.includes('NON_COMPLIANT');
  return {
    nonCompliant,
    commentRequired: naComment || ncComment,
    photoRequired: nonCompliant && control.requirePhotoOn.includes('NON_COMPLIANT'),
    commentReason: ncComment ? 'NON_COMPLIANT' : naComment ? 'NA' : null,
  };
}

export interface ControlCardProps {
  control: ControlItem;
  answer: ControlAnswer | undefined;
  onAnswer: (value: ControlValue | null) => void;
  onNote: (note: string) => void;
  onPatch?: (patch: Partial<ControlAnswer>) => void;
  siteId?: string;
  surveyId?: string;
  lang: string;
  sourceLanguage: string;
  actions: CaptureActions;
  disabled?: boolean;
  /** Validation errors to show (after a submit attempt). */
  errors?: string[];
  flagNote?: string;
  carried?: CarriedFinding;
  number?: number;
}

export function ControlCard(p: ControlCardProps) {
  const { control, answer, lang, sourceLanguage: src, disabled, actions } = p;
  const { t, fmt } = useI18n();
  const { api } = useServices();
  const toast = useToast();
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [copilotLang, setCopilotLang] = useState<string>('en');

  const value = answer?.value ?? null;
  const req = controlRequirements(control, value);
  const question = localize(control.question, lang, src);
  const help = control.help ? localize(control.help, lang, src) : '';
  const note = answer?.note ?? '';
  const photos = answer?.photos ?? [];
  const commentMissing = req.commentRequired && !note.trim();
  const photoMissing = req.photoRequired && photos.length === 0;
  const canStopTheLine = req.nonCompliant && (control.severity === 'CRITICAL' || control.severity === 'HIGH' || !!control.lifeSafetyCritical);

  const tone = (v: ControlValue) => (v === 'NA' ? 'neutral' : isNonCompliant(control, v) ? 'danger' : 'success');

  const scan = answer?.checkpointScan;
  const [scanning, setScanning] = useState(false);
  const scanCheckpoint = actions.scanCheckpoint;
  const onTag = useCallback(
    (tag: { payload: string; method: 'QR' | 'NFC' }) => {
      setScanning(false);
      if (!scanCheckpoint) return;
      void scanCheckpoint(control.code, tag).then(() => toast.show(t('ui.capture.tag.scannedToast'), 'success'));
    },
    [scanCheckpoint, control.code, toast, t],
  );

  const handleAiPolish = () => {
    if (disabled) return;
    const res = polishAndTranslateObservation({
      controlCode: control.code,
      question,
      severity: control.severity,
      rawNote: note,
      targetLanguage: copilotLang,
    });
    const nextNote =
      copilotLang !== 'en' && res.translatedLanguage !== 'en'
        ? `${res.polishedObservation}\n\n[${res.translatedLanguage.toUpperCase()}] ${res.translatedObservation}`
        : res.polishedObservation;
    p.onNote(nextNote);
    toast.show('Observation polished with AI Co-Pilot (ISO 45001 / ISO 18788)', 'success');
  };

  const triggerStopTheLine = async () => {
    if (!p.siteId || answer?.stopTheLineEscalatedAt) return;
    setEscalating(true);
    try {
      await api.post('/safety/escalations', {
        siteId: p.siteId,
        surveyId: p.surveyId,
        controlCode: control.code,
        title: `${control.code} — ${question}`,
        description: note.trim() || question,
        severity: control.severity === 'CRITICAL' ? 'CRITICAL' : 'HIGH',
      });
      p.onPatch?.({ stopTheLineEscalatedAt: new Date().toISOString() });
      toast.show(t('ui.safety.escalation.triggeredToast'), 'danger');
    } catch {
      // Offline or transient failure: still record local escalation timestamp so the field reporter has immediate feedback
      p.onPatch?.({ stopTheLineEscalatedAt: new Date().toISOString() });
      toast.show(t('ui.safety.escalation.triggeredToast'), 'danger');
    } finally {
      setEscalating(false);
    }
  };

  return (
    <Card
      id={`item-${control.id}`}
      className={cx('scroll-mt-24 p-4', p.flagNote && 'border-warning ring-2 ring-warning/30', disabled && !p.flagNote && 'opacity-80')}
    >
      {p.flagNote && (
        <Banner tone="warning" title={t('ui.capture.analystNote')} className="mb-3">
          {p.flagNote}
        </Banner>
      )}
      {p.carried && (
        <Banner tone="danger" title={t('ui.capture.carriedTitle', { id: p.carried.humanId })} className="mb-3">
          {t('ui.capture.carriedBody', { date: fmt.date(p.carried.dueDate) })}
        </Banner>
      )}
      <div className="mb-3 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
            {p.number !== undefined && <span className="font-mono font-semibold text-fg/70">{p.number}.</span>}
            <span className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs font-semibold text-fg/85 ring-1 ring-inset ring-border">{control.code}</span>
            {control.core && <Badge tone="brand">{t('ui.capture.core')}</Badge>}
            {answer?.baselineVerified && (
              <Badge tone="success" icon="check">
                {t('ui.excellence.delta.verifiedBadge')}
              </Badge>
            )}
            {control.checkpoint !== 'NONE' && <CheckpointBadge requirement={control.checkpoint} scan={scan} />}
            {control.checkpoint !== 'NONE' && !disabled && scanCheckpoint && (
              <button
                type="button"
                onClick={() => setScanning(true)}
                className="inline-flex items-center gap-1 rounded bg-surface-2 px-2 py-0.5 text-xs font-medium text-fg/85 ring-1 ring-inset ring-border hover:bg-surface"
              >
                <Icon name="scan" size={13} />
                {scan && scan.status !== 'REJECTED' ? t('ui.capture.tag.rescan') : t('ui.capture.tag.scan')}
              </button>
            )}
          </div>
          <p className="text-base font-semibold leading-snug sm:text-lg">{question}</p>
          {scan?.status === 'REJECTED' && (
            <p className="mt-1 text-sm font-medium text-danger">
              {t(`ui.capture.tag.error.${['TAG_INVALID', 'TAG_RETIRED', 'TAG_OTHER_SITE'].includes(scan.error ?? '') ? scan.error : 'UNKNOWN'}`)}
            </p>
          )}
          {scan?.status === 'VERIFIED' && (scan.flags ?? []).length > 0 && (
            <p className="mt-1 text-sm text-warning">{(scan.flags ?? []).map((f) => t(`ui.capture.tag.flags.${f}`)).join(' · ')}</p>
          )}
        </div>
        {help && <HelpButton title={question} text={help} />}
      </div>

      <Segmented<ControlValue>
        label={question}
        disabled={disabled}
        value={value}
        allowDeselect
        onChange={(v) => p.onAnswer(v)}
        options={[
          { value: 'YES', label: t('common.yes'), tone: tone('YES') },
          { value: 'NO', label: t('common.no'), tone: tone('NO') },
          ...(control.allowNA ? [{ value: 'NA' as const, label: t('common.na'), tone: 'neutral' as const }] : []),
        ]}
      />

      {req.nonCompliant && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-danger">
            <Badge tone={SEVERITY_TONE[control.severity] ?? 'neutral'}>{t(`severity.${control.severity}`)}</Badge>
            {t('ui.capture.notInPlace')}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {p.onPatch && (
              <Button
                size="sm"
                variant="secondary"
                icon="mapPin"
                disabled={disabled}
                onClick={() => setPinModalOpen(true)}
              >
                {answer?.pin
                  ? t('ui.excellence.pin.pinned', { x: Math.round(answer.pin.x), y: Math.round(answer.pin.y) })
                  : t('ui.excellence.pin.button')}
              </Button>
            )}
            {canStopTheLine && p.siteId && (
              <Button
                size="sm"
                variant={answer?.stopTheLineEscalatedAt ? 'secondary' : 'danger'}
                icon="alert"
                loading={escalating}
                disabled={disabled || !!answer?.stopTheLineEscalatedAt}
                onClick={triggerStopTheLine}
              >
                {answer?.stopTheLineEscalatedAt
                  ? t('ui.safety.escalation.triggerActive')
                  : t('ui.safety.escalation.triggerButton')}
              </Button>
            )}
          </div>
        </div>
      )}

      {(value !== null || note) && (
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-xs text-muted">
              {req.commentRequired
                ? t(req.commentReason === 'NA' ? 'ui.capture.commentRequiredNa' : 'ui.capture.commentRequiredNc')
                : t('ui.capture.commentInternal')}
            </span>
            {!disabled && (
              <div className="flex flex-wrap items-center gap-1.5">
                <div className="inline-flex items-center rounded-md border border-border bg-surface-2 p-0.5 text-xs">
                  <select
                    aria-label="AI Co-Pilot language"
                    value={copilotLang}
                    onChange={(e) => setCopilotLang(e.target.value)}
                    className="bg-transparent px-1.5 py-0.5 text-[11px] font-semibold text-muted focus:outline-none"
                  >
                    <option value="en">EN</option>
                    <option value="es">EN + ES</option>
                    <option value="pt">EN + PT</option>
                    <option value="fr">EN + FR</option>
                    <option value="de">EN + DE</option>
                    <option value="ar">EN + AR</option>
                  </select>
                  <button
                    type="button"
                    onClick={handleAiPolish}
                    className="rounded bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand hover:bg-brand/20"
                    title="Transform shorthand note into ISO-grade observation & translate"
                  >
                    ✨ AI Polish
                  </button>
                </div>
                <VoiceNoteButton
                  currentNote={note}
                  disabled={disabled}
                  onAppend={(nextNote) => p.onNote(nextNote)}
                />
              </div>
            )}
          </div>
          <Field
            label={t('ui.capture.comment')}
            required={req.commentRequired}
            error={p.errors?.includes('COMMENT_REQUIRED') && commentMissing ? t('ui.validation.COMMENT_REQUIRED') : null}
          >
            {(id, describedBy) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                rows={2}
                disabled={disabled}
                invalid={!!p.errors?.includes('COMMENT_REQUIRED') && commentMissing}
                value={note}
                onChange={(e) => p.onNote(e.target.value)}
              />
            )}
          </Field>
        </div>
      )}

      {(value !== null || photos.length > 0) && (
        <div className="mt-3 flex flex-col gap-1.5">
          <span className="text-sm font-semibold">
            {t('ui.capture.evidence')}
            {req.photoRequired && <span className="ms-0.5 text-danger">*</span>}
          </span>
          <PhotoInput
            ids={photos}
            required={photoMissing}
            disabled={disabled}
            getUrl={actions.fileUrl}
            // The answers are updated by the actions, from their current state (not this render's photo list).
            onAdd={async (files) => {
              await actions.addFiles({ control: control.code }, 'PHOTO', files);
            }}
            onRemove={(id) => actions.removeFile(id)}
          />
          {p.errors?.includes('PHOTO_REQUIRED') && photoMissing && <p className="text-sm font-medium text-danger">{t('ui.validation.PHOTO_REQUIRED')}</p>}
        </div>
      )}

      {p.errors?.filter((e) => e !== 'COMMENT_REQUIRED' && e !== 'PHOTO_REQUIRED').map((e) => (
        <p key={e} className="mt-2 text-sm font-medium text-danger" role="alert">
          {t(`ui.validation.${e}`)}
        </p>
      ))}

      {pinModalOpen && p.onPatch && (
        <FloorplanPinModal
          open={pinModalOpen}
          onClose={() => setPinModalOpen(false)}
          controlCode={control.code}
          initialPin={answer?.pin}
          onSave={(pin) => p.onPatch?.({ pin })}
        />
      )}
      {scanning && <TagScanner open onClose={() => setScanning(false)} onScan={onTag} title={question} />}
    </Card>
  );
}

/** Whether the question's tag is needed, and where its scan stands (ADR-0034). */
export function CheckpointBadge({ requirement, scan }: { requirement: 'OPTIONAL' | 'REQUIRED'; scan: ControlAnswer['checkpointScan'] }) {
  const { t } = useI18n();
  if (scan?.status === 'VERIFIED')
    return (
      <Badge tone={(scan.flags ?? []).length ? 'warning' : 'success'} icon="shield">
        {t('ui.capture.tag.verified')} · {scan.method}
      </Badge>
    );
  if (scan?.status === 'PENDING') return <Badge tone="info">{t('ui.capture.tag.pending')}</Badge>;
  if (scan?.status === 'REJECTED') return <Badge tone="danger">{t('ui.capture.tag.rejected')}</Badge>;
  return <Badge tone={requirement === 'REQUIRED' ? 'warning' : 'neutral'}>{requirement === 'REQUIRED' ? t('ui.capture.tag.required') : t('ui.capture.tag.optional')}</Badge>;
}
